/**
 * Traffic Source Detection Utility
 * 
 * Captures the traffic source on first page load using:
 * 1. UTM parameters from URL (paid/campaign traffic)
 * 2. document.referrer (organic search, social, referral)
 * 3. Direct detection (no referrer)
 * 
 * Stores in localStorage so it persists across the session.
 * Only captures once per session (first touch attribution).
 */

const STORAGE_KEY = 'la_traffic_source';

// Classify channel based on source + medium
const classifyChannel = (source, medium) => {
  const src = (source || '').toLowerCase();
  const med = (medium || '').toLowerCase();

  // Paid Advertising — check for cpc, paid_social, display, video, native
  // Also handle Google Ads ValueTrack parameters like {ifmobile:mobile}{ifnotmobile:cpc}
  if (
    med === 'cpc' || med === 'paid_social' || med === 'display' || 
    med === 'video' || med === 'native' ||
    med.includes('cpc') || med.includes('paid') ||
    med.includes('{if') // Google Ads ValueTrack patterns
  ) {
    return 'Paid Advertising';
  }

  // Email
  if (med === 'email') return 'Email';

  // Organic Social
  if (med === 'social') return 'Organic Social';

  // Organic Search
  if (med === 'organic') return 'Organic Search';

  // Referral
  if (med === 'referral') return 'Referral';

  // Direct
  if (src === 'direct' || med === 'none') return 'Direct';

  // If source is a known ad platform, assume paid
  const paidSources = ['google', 'facebook', 'instagram', 'linkedin', 'tiktok', 'twitter', 'pinterest', 'youtube'];
  if (paidSources.includes(src) && med && med !== 'organic' && med !== 'social') {
    return 'Paid Advertising';
  }

  return 'Other';
};

// Detect source from document.referrer
const detectFromReferrer = () => {
  const referrer = document.referrer;
  console.log("referrer", !referrer,referrer)

  if (!referrer) {
    return 
  }

  let referrerHost = '';
  try {
    referrerHost = new URL(referrer).hostname.toLowerCase();
  } catch (e) {
    return { source: 'direct', medium: 'none', channel: 'Direct' };
  }

  // Same-site referrer = direct
  if (referrerHost === window.location.hostname) {
    return { source: 'direct', medium: 'none', channel: 'Direct' };
  }

  // Organic Search engines
  if (/google\./i.test(referrerHost)) return { source: 'google', medium: 'organic', channel: 'Organic Search' };
  if (/bing\./i.test(referrerHost)) return { source: 'bing', medium: 'organic', channel: 'Organic Search' };
  if (/yahoo\./i.test(referrerHost)) return { source: 'yahoo', medium: 'organic', channel: 'Organic Search' };
  if (/duckduckgo/i.test(referrerHost)) return { source: 'duckduckgo', medium: 'organic', channel: 'Organic Search' };

  // Social platforms
  if (/facebook\.com|fb\.com/i.test(referrerHost)) return { source: 'facebook', medium: 'social', channel: 'Organic Social' };
  if (/instagram\.com/i.test(referrerHost)) return { source: 'instagram', medium: 'social', channel: 'Organic Social' };
  if (/linkedin\.com/i.test(referrerHost)) return { source: 'linkedin', medium: 'social', channel: 'Organic Social' };
  if (/twitter\.com|x\.com/i.test(referrerHost)) return { source: 'twitter', medium: 'social', channel: 'Organic Social' };
  if (/youtube\.com/i.test(referrerHost)) return { source: 'youtube', medium: 'social', channel: 'Organic Social' };
  if (/tiktok\.com/i.test(referrerHost)) return { source: 'tiktok', medium: 'social', channel: 'Organic Social' };
  if (/pinterest\.com/i.test(referrerHost)) return { source: 'pinterest', medium: 'social', channel: 'Organic Social' };

  // Messaging platforms
  if (/whatsapp\.com|wa\.me/i.test(referrerHost)) return { source: 'whatsapp', medium: 'social', channel: 'Messaging' };
  if (/t\.me|telegram/i.test(referrerHost)) return { source: 'telegram', medium: 'social', channel: 'Messaging' };
  if (/messenger\.com/i.test(referrerHost)) return { source: 'messenger', medium: 'social', channel: 'Messaging' };

  // Everything else = Referral
  return { source: referrerHost, medium: 'referral', channel: 'Referral' };
};

/**
 * Capture traffic source on first visit.
 * Call this once on app boot (e.g., in ReactRouteAll or App component).
 * Uses first-touch attribution — only stores if not already captured.
 */
export const captureTrafficSource = () => {
  

  const params = new URLSearchParams(window.location.search);
  let trafficData;

  // 1. Check UTM params first (paid/campaign traffic)
  if (params.get('utm_source')) {
    const source = params.get('utm_source');
    const medium = params.get('utm_medium') || '';
    trafficData = {
      source,
      medium,
      campaign: params.get('utm_campaign') || '',
      content: params.get('utm_content') || '',
      term: params.get('utm_term') || '',
      id: params.get('utm_id') || '',
      channel: classifyChannel(source, medium),
      referrer: document.referrer || '',
      landing_page: window.location.pathname,
      timestamp: new Date().toISOString()
    };
  } else {
    // 2. No UTMs — detect from referrer
       if (!document.referrer) return null;
    const detected = detectFromReferrer();
    trafficData = {
      ...detected,
      campaign: '',
      content: '',
      term: '',
      id: '',
      referrer: document.referrer || '',
      landing_page: window.location.pathname,
      timestamp: new Date().toISOString()
    };
  }

  // Store in localStorage
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trafficData));
  return trafficData;
};

/**
 * Get the stored traffic source data.
 * Returns null if not yet captured.
 */
export const getTrafficSource = () => {
  const data = sessionStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : null;
};

/**
 * Clear stored traffic source (e.g., on logout).
 */
export const clearTrafficSource = () => {
  sessionStorage.removeItem(STORAGE_KEY);
};

export default { captureTrafficSource, getTrafficSource, clearTrafficSource };
